import { cva, type VariantProps } from "class-variance-authority";
import Image from "next/image";
import type { ComponentPropsWithoutRef } from "react";

import { cn } from "@/lib/utils";

const logoVariants = cva("inline-flex shrink-0 select-none", {
	variants: {
		variant: {
			default: "items-end leading-none",
			text: "items-end leading-none",
			logo: "items-center justify-center leading-none",
			combo: "items-center leading-none",
		},
		size: {
			sm: "",
			md: "",
			lg: "",
			xl: "",
		},
	},
	compoundVariants: [
		{
			variant: "combo",
			size: "sm",
			className: "gap-[0.28em]",
		},
		{
			variant: "combo",
			size: "md",
			className: "gap-[0.32em]",
		},
		{
			variant: "combo",
			size: "lg",
			className: "gap-[0.36em]",
		},
		{
			variant: "combo",
			size: "xl",
			className: "gap-[0.4em]",
		},
	],
	defaultVariants: {
		variant: "default",
		size: "md",
	},
});

const markVariants = cva("inline-flex shrink-0", {
	variants: {
		variant: {
			logo: "",
			combo: "",
		},
		size: {
			sm: "",
			md: "",
			lg: "",
			xl: "",
		},
	},
	compoundVariants: [
		{
			variant: "logo",
			size: "sm",
			className: "h-[1.3em]",
		},
		{
			variant: "logo",
			size: "md",
			className: "h-[1.44em]",
		},
		{
			variant: "logo",
			size: "lg",
			className: "h-[1.58em]",
		},
		{
			variant: "logo",
			size: "xl",
			className: "h-[2.5em]",
		},
		{
			variant: "combo",
			size: "sm",
			className: "h-[1.62em]",
		},
		{
			variant: "combo",
			size: "md",
			className: "h-[1.8em]",
		},
		{
			variant: "combo",
			size: "lg",
			className: "h-[1.98em]",
		},
		{
			variant: "combo",
			size: "xl",
			className: "h-[2.18em]",
		},
	],
	defaultVariants: {
		size: "md",
	},
});

const wordmarkVariants = cva(
	"inline-flex shrink-0 items-end font-black font-exo italic leading-none tracking-wide",
	{
		variants: {
			variant: {
				default: "",
				text: "",
				combo: "",
			},
			size: {
				sm: "",
				md: "",
				lg: "",
				xl: "",
			},
		},
		compoundVariants: [
			{
				variant: "combo",
				size: "sm",
				className: "translate-y-[0.06em]",
			},
			{
				variant: "combo",
				size: "md",
				className: "translate-y-[0.065em]",
			},
			{
				variant: "combo",
				size: "lg",
				className: "translate-y-[0.07em]",
			},
			{
				variant: "combo",
				size: "xl",
				className: "translate-y-[0.075em]",
			},
		],
		defaultVariants: {
			variant: "default",
			size: "md",
		},
	},
);

type LogoProps = ComponentPropsWithoutRef<"span"> &
	VariantProps<typeof logoVariants> & {
		dotClassName?: string;
		markClassName?: string;
		textClassName?: string;
	};

export default function Logo({
	className,
	dotClassName,
	markClassName,
	size = "md",
	textClassName,
	variant = "default",
	...props
}: LogoProps) {
	const showMark = variant === "logo" || variant === "combo";
	const showWordmark =
		variant === "default" || variant === "text" || variant === "combo";
	const markVariant = variant === "logo" ? "logo" : "combo";
	const wordmarkVariant = variant === "combo" ? "combo" : "default";

	return (
		<span
			data-size={size}
			data-slot="brand-logo"
			data-variant={variant}
			className={cn(logoVariants({ variant, size, className }))}
			{...props}
		>
			{showMark ? (
				<span
					aria-hidden="true"
					className={cn(
						markVariants({ variant: markVariant, size }),
						markClassName,
					)}
				>
					<Image
						src="/assets/logo_slanted.svg"
						alt=""
						width={320}
						height={320}
						className="block h-full w-auto"
						draggable="false"
					/>
					{/* <img
						src="/assets/logo_slanted_white.svg"	
						alt=""
						className="hidden h-full w-auto dark:block"
						draggable="false"
					/> */}
				</span>
			) : null}

			{showWordmark ? (
				<span
					className={cn(
						wordmarkVariants({ variant: wordmarkVariant, size }),
						textClassName,
					)}
				>
					<span>Clutch</span>
					<span
						aria-hidden="true"
						className={cn("text-cadre-teal", dotClassName)}
					>
						.
					</span>
				</span>
			) : (
				<span className="sr-only">Clutch</span>
			)}
		</span>
	);
}
